import { useEffect, useRef, useState } from "react";
import { FolderOpen, Upload, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import api, { fileUrl, formatApiError } from "../lib/api";
import AudioPlayer from "../components/AudioPlayer";
import { FadeUp } from "../components/Reveal";

const KIND_STYLE = {
  master: "bg-gold/15 text-[#8a6d1d] border border-gold/40",
  mix: "bg-purple-100 text-purple-800",
  instrumental: "bg-sky-100 text-sky-800",
  acapella: "bg-rose-100 text-rose-700",
  revision: "bg-amber-100 text-amber-800",
  other: "bg-slate-100 text-slate-600",
};

function ProjectCard({ project }) {
  const [open, setOpen] = useState(false);
  const [files, setFiles] = useState(null);
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const load = () => api.get(`/projects/${project.project_id}/files`).then((r) => setFiles(r.data)).catch(() => {});
  const toggle = () => {
    if (!open && files === null) load();
    setOpen(!open);
  };

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("name", f.name);
      await api.post(`/projects/${project.project_id}/client-files`, fd);
      toast.success("File sent to Empress");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const byVersion = (files || []).reduce((acc, f) => {
    (acc[f.version] = acc[f.version] || []).push(f);
    return acc;
  }, {});
  const versions = Object.keys(byVersion).sort().reverse();

  return (
    <FadeUp className="bg-white rounded-3xl border border-purple-100 overflow-hidden" data-testid={`project-card-${project.project_id}`}>
      <button onClick={toggle} className="w-full flex items-center gap-4 p-6 sm:p-7 text-left" data-testid={`project-toggle-${project.project_id}`}>
        <span className="w-12 h-12 rounded-2xl bg-lilac-mist flex items-center justify-center text-purple-700 shrink-0"><FolderOpen size={20} /></span>
        <span className="flex-1 min-w-0">
          <span className="block font-serif text-xl text-obsidian truncate">{project.title}</span>
          <span className="block text-xs text-slate-400 mt-0.5">{project.artist} · {project.status}</span>
        </span>
        <ChevronDown size={18} className={`text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="px-6 sm:px-7 pb-7 border-t border-purple-50">
          {files === null && <p className="text-sm text-slate-400 py-6">Loading files…</p>}
          {versions.map((v) => (
            <div key={v} className="mt-6">
              <p className="text-xs font-mono uppercase tracking-[0.25em] text-gold mb-3">Version {v}</p>
              <div className="space-y-3">
                {byVersion[v].map((f) => (
                  <div key={f.file_id}>
                    <div className="flex items-center gap-3 mb-2 px-1">
                      <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full ${KIND_STYLE[f.kind] || KIND_STYLE.other}`}>{f.kind}</span>
                      <span className="text-xs text-slate-400">{(f.size / 1048576).toFixed(1)} MB</span>
                    </div>
                    {f.mime.startsWith("audio/") ? (
                      <AudioPlayer src={fileUrl(f.file_id, "stream")} title={f.name} subtitle={`${f.kind} · ${f.version}`}
                        downloadHref={fileUrl(f.file_id, "download")} testid={`player-${f.file_id}`} />
                    ) : (
                      <a href={fileUrl(f.file_id, "download")} data-testid={`download-${f.file_id}`}
                        className="flex items-center justify-between bg-lilac-mist rounded-2xl px-5 py-4 text-sm text-obsidian hover:border-gold border border-transparent transition-colors">
                        {f.name} <span className="text-xs text-purple-700">Download</span>
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
          {files && files.length === 0 && <p className="text-sm text-slate-400 py-6 italic">No files shared yet — Empress will post mixes here.</p>}
          <div className="mt-7">
            <input ref={inputRef} type="file" className="hidden" onChange={upload} data-testid={`revision-input-${project.project_id}`} />
            <button onClick={() => inputRef.current?.click()} disabled={uploading} data-testid={`revision-upload-${project.project_id}`}
              className="flex items-center gap-2 px-6 py-3 rounded-full border border-dashed border-purple-300 text-sm text-purple-700 hover:border-gold hover:text-gold transition-colors disabled:opacity-50">
              <Upload size={15} /> {uploading ? "Uploading…" : "Send a revision or reference"}
            </button>
          </div>
        </div>
      )}
    </FadeUp>
  );
}

export default function PortalProjects() {
  const [projects, setProjects] = useState([]);
  useEffect(() => {
    api.get("/projects/mine").then((r) => setProjects(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="portal-projects">
      <FadeUp>
        <h1 className="font-serif text-3xl sm:text-4xl text-obsidian">Your <span className="italic text-purple-700">projects</span></h1>
        <p className="text-sm text-slate-500 mt-2">Mixes, masters, instrumentals and acapellas — organized by project and version. Listen before you download.</p>
      </FadeUp>
      <div className="mt-10 space-y-6">
        {projects.map((p) => <ProjectCard key={p.project_id} project={p} />)}
        {projects.length === 0 && <p className="text-sm text-slate-400 italic">No projects yet. Once you book a mix or master, your music will live here.</p>}
      </div>
    </div>
  );
}
